import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { epinScanSamples, epinScanDefault } from '@/mocks/epin';
import type { EpinScanResult } from '@/mocks/epin';
import QrPattern from './QrPattern';

const TRACE_PATH = '/trace/passport';

const PUBLIC_ORIGIN = 'https://ltmzqm.amazingsite.co';

function buildTraceUrl(code: string) {
  return `${PUBLIC_ORIGIN}${TRACE_PATH}?code=${encodeURIComponent(code)}`;
}

function ResultPanel({ result }: { result: EpinScanResult }) {
  const isOk = result.tone === 'ok';

  const infoItems: { label: string; value: string }[] = [
    { label: '品类', value: result.category },
    { label: '规格', value: result.spec },
    { label: '批次', value: result.batch },
    { label: '采收日期', value: result.harvestDate },
    { label: '产地', value: result.origin },
    { label: '生产主体', value: result.producer },
  ];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
      {/* 结果头 */}
      <div className="px-5 md:px-6 py-5 border-b border-background-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-lg text-foreground-950">{result.product}</h3>
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-qr-code-line"></i>
              {result.code}
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap border ${
              isOk
                ? 'bg-secondary-100 text-secondary-900 border-secondary-200'
                : 'bg-foreground-100 text-foreground-800 border-foreground-300'
            }`}
          >
            <i className={isOk ? 'ri-verified-badge-line' : 'ri-error-warning-line'}></i>
            {result.status}
          </span>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="px-5 md:px-6 py-5">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground-500 tracking-wide">
          <i className="ri-file-list-3-line"></i>
          产品基本信息
        </div>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {infoItems.map((item) => (
            <div key={item.label} className="flex items-baseline gap-3">
              <dt className="w-[72px] shrink-0 text-xs text-foreground-500">{item.label}</dt>
              <dd className="text-sm text-foreground-900 leading-relaxed">{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* 质检结果 */}
      <div className="px-5 md:px-6 py-5 border-t border-background-200">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground-500 tracking-wide">
          <i className="ri-shield-check-line"></i>
          质量检测结果
        </div>
        <ul className="mt-3 space-y-2">
          {result.qc.map((row) => {
            const pass = row.result.includes('合格') && !row.result.includes('不合格');
            return (
              <li
                key={row.name}
                className="flex items-center justify-between gap-4 rounded-md bg-background-100 border border-background-200 px-3.5 py-2.5"
              >
                <div className="min-w-0">
                  <div className="text-sm text-foreground-900 truncate">{row.name}</div>
                  <div className="mt-0.5 text-xs text-foreground-500">{row.org}</div>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    pass ? 'bg-secondary-100 text-secondary-900' : 'bg-foreground-100 text-foreground-800'
                  }`}
                >
                  <i className={pass ? 'ri-check-line' : 'ri-close-line'}></i>
                  {row.result}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* 物流节点 */}
      <div className="px-5 md:px-6 py-5 border-t border-background-200">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground-500 tracking-wide">
          <i className="ri-route-line"></i>
          流通轨迹 · {result.nodes.length} 个节点
        </div>
        <ol className="mt-4">
          {result.nodes.map((node, idx) => (
            <li key={node.node} className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${node.icon} text-sm`}></i>
                </span>
                {idx < result.nodes.length - 1 && <span className="w-px flex-1 my-1 bg-background-300"></span>}
              </div>
              <div className="flex-1 pb-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-heading font-bold text-sm text-foreground-950">{node.node}</span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{node.time}</span>
                </div>
                <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{node.desc}</p>
                <p className="mt-0.5 text-xs text-foreground-400">{node.place}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function EpinScanDemo() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<EpinScanResult | null>(null);
  const [notFound, setNotFound] = useState(false);

  const scan = (value?: string) => {
    const target = (value ?? code).trim().toUpperCase();
    setCode(target);
    if (!target) {
      setResult(null);
      setNotFound(false);
      return;
    }
    const found = epinScanSamples.find((s) => s.code.toUpperCase() === target);
    if (found) {
      setResult(found);
      setNotFound(false);
    } else {
      setResult(null);
      setNotFound(true);
    }
  };

  // 支持通过二维码扫码进入：/cases/epin-food-safety?code=xxx 自动回填并查询
  useEffect(() => {
    const initial = searchParams.get('code');
    if (initial) scan(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 默认展示一条完整示例档案（图2 样式），未查询到编码时回退为待验证态
  const active: EpinScanResult | null = notFound ? null : result ?? epinScanDefault;
  const activeOk = active?.tone === 'ok';
  const activePass = active ? !active.qc.some((q) => q.result.includes('不合格')) : false;

  const badgeText = !active ? '待验证' : activeOk ? '有效可售' : '已冻结 · 待复检';
  const badgeClass = !active
    ? 'bg-background-100 text-foreground-600 border border-background-300'
    : activeOk
    ? 'bg-secondary-100 text-secondary-900 border border-secondary-200'
    : 'bg-foreground-100 text-foreground-700 border border-foreground-300';
  const badgeIcon = !active ? 'ri-time-line' : activeOk ? 'ri-checkbox-circle-fill' : 'ri-error-warning-line';

  const cardRows: { label: string; value: string; pass?: boolean }[] = [
    { label: '商品', value: active ? active.product : '—' },
    { label: '批次', value: active ? active.batch : '—' },
    { label: '产区', value: active ? active.origin : '—' },
    { label: '检测', value: !active ? '待验证' : activePass ? '合格' : '不合格', pass: active ? activePass : undefined },
  ];

  const displayCode = active ? active.code : code || 'EPIN-DEMO-0001';
  const traceUrl = buildTraceUrl(displayCode);

  const showResultPanel = Boolean(result) || notFound;

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          title="模拟扫码溯源"
          subtitle="扫码或输入一个 ePin 编码，实时展示该产品的产地、质检结果与物流流通节点，体验一物一码的溯源能力。"
        />

        <div
          className={`mt-10 md:mt-12 grid gap-6 lg:gap-8 items-start ${
            showResultPanel
              ? 'grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr]'
              : 'grid-cols-1 lg:grid-cols-[minmax(0,420px)] lg:justify-center'
          }`}
        >
          {/* 扫码输入 */}
          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-qr-scan-2-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading font-bold text-base text-foreground-950">扫码 </h3>
                  <p className="text-xs text-foreground-500">ePin 食安码 · 一物一码</p>
                </div>
              </div>

              {/* 食安码卡片 */}
              <div className="mt-6 rounded-lg p-4 bg-gradient-to-br from-accent-50 via-background-50 to-secondary-50 border border-accent-100">
                  <div className="flex items-center justify-end gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${badgeClass}`}>
                      <i className={badgeIcon}></i>
                      {badgeText}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center justify-center rounded-lg bg-background-50 border border-background-200 px-4 py-4">
                    <QrPattern value={traceUrl} size={200} />
                  </div>

                  <p className="mt-3 text-center font-mono text-[11px] text-foreground-400 truncate">
                    {displayCode}
                  </p>

                  <dl className="mt-4 pt-4 border-t border-background-200 space-y-2.5">
                    {cardRows.map((row) => (
                      <div key={row.label} className="flex items-start justify-between gap-4">
                        <dt className="pt-0.5 text-xs text-foreground-500 whitespace-nowrap">{row.label}</dt>
                        <dd className="min-w-0 text-right text-sm font-medium leading-snug text-foreground-900">
                          {row.pass !== undefined && (
                            <i
                              className={`mr-1 align-middle ${
                                row.pass ? 'ri-checkbox-circle-fill text-secondary-500' : 'ri-error-warning-fill text-foreground-500'
                              }`}
                            ></i>
                          )}
                          <span className={row.pass ? 'text-secondary-700' : ''}>{row.value}</span>
                        </dd>
                      </div>
                    ))}
                  </dl>

                  <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-foreground-400">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-shield-check-line"></i>可信留痕
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-route-line"></i>全程可追
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-verified-badge-line"></i>平台核验
                    </span>
                  </div>
              </div>

              <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-foreground-400">
                <i className="ri-scan-2-line"></i>
                用手机扫描二维码，可在站内打开该编码的完整溯源档案
              </p>

              {/* 输入 */}
              <form
                className="mt-6 flex flex-col sm:flex-row gap-2.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  scan();
                }}
              >
                <div className="relative flex-1">
                  <i className="ri-qr-scan-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="输入 ePin 编码"
                    className="w-full text-sm rounded-md border border-background-300 bg-background-50 pl-9 pr-3 py-2.5 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
                >
                  <i className="ri-search-eye-line"></i>
                  查询
                </button>
              </form>

              {/* 示例编码 */}
              <div className="mt-5">
                <p className="text-xs text-foreground-500">试试这些示例编码：</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {epinScanSamples.map((s) => (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => scan(s.code)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-300 bg-background-100 text-xs text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:border-accent-400 hover:text-accent-800"
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          s.tone === 'ok' ? 'bg-accent-500' : 'bg-foreground-500'
                        }`}
                      ></span>
                      {s.product.replace(/（.*?）/, '')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* 查询结果 */}
          {showResultPanel && (
            <Reveal delay={100}>
              {result ? (
                <ResultPanel result={result} />
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 min-h-[420px] flex flex-col items-center justify-center text-center px-6 py-14">
                  <span className="w-14 h-14 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-500">
                    <i className="ri-qr-scan-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-foreground-950">未查询到该编码</h3>
                  <p className="mt-2 text-sm text-foreground-600 max-w-sm leading-relaxed">
                    编码 <span className="font-medium text-foreground-900">{code}</span> 暂无溯源记录。请检查编码是否正确，或点击左侧示例编码体验。
                  </p>
                </div>
              )}
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}